<template>
  <form
    v-if="isRevealed"
    class="space-y-4 p-4"
    @submit="onSubmit"
  >
    <FormInputText label="Label" name="label" />

    <div class="grid grid-cols-2 gap-4">
      <FormInputSelect
        label="Min Options"
        name="minOptions"
        :options="minOptionsOptions"
        disable-null
      />
      <FormInputSelect
        label="Max Options"
        name="maxOptions"
        :options="maxOptionsOptions"
        disable-null
      />
    </div>

    <div class="h-px w-full bg-background-fg/8" />
    <section class="mb-2">
      <label
        class="mb-1 block text-left text-sm font-medium text-background-fg/70"
      >
        Options
      </label>
      <!-- @vue-ignore -->
      <VDraggable
        v-model="optionFieldsModel"
        item-key="key"
        handle="[data-handle]"
        tag="ol"
        v-bind="dragOptions"
        class="mb-2 space-y-2"
      >
        <template #item="{ index }">
          <li
            class="flex items-center justify-around space-x-2 bg-background p-2 shadow"
          >
            <div>
              <button
                type="button"
                data-handle
                class="cursor-grab"
              >
                <IconThreeBars
                  class="size-5 text-gray-400"
                />
              </button>
            </div>
            <FormInputProductModifierOption
              :name="`options.${index}`"
            />
            <button
              class="flex items-center justify-center"
              @click="
                (event) => handleRemoveOption(event, index)
              "
            >
              <IconTrash />
            </button>
          </li>
        </template>
      </VDraggable>

      <div class="flex justify-center">
        <ButtonAdd @click="addOption">
          Add Option
        </ButtonAdd>
      </div>
    </section>

    <div class="grid grid-cols-2 gap-2">
      <ButtonSecondary @click="cancel">
        Back
      </ButtonSecondary>
      <FormSubmitButton>
        Save Modifier Group
      </FormSubmitButton>
    </div>
  </form>
</template>

<script setup lang="ts">
import VDraggable from "vuedraggable"
import { useFieldArray } from "vee-validate"
import { ApiError } from "@/api"
import type { ProductModifierGroupFormInput } from "@/composables/forms/useProductModifierGroupForm"

const modifierGroupId = ref<string>()

const $style = useCssModule()
const { reveal, isRevealed, confirm, cancel, onReveal } =
  useConfirmDialog<
    ProductModifierGroupFormInput & { id?: string },
    string
  >()

onReveal((params) => {
  resetForm()
  modifierGroupId.value = params.id
  setValues(params)
})

defineExpose({ reveal, isRevealed })

const minOptionsOptions = Array.from(
  { length: 11 },
  (_, i) => ({
    value: i,
    label: String(i),
  })
)
const maxOptionsOptions = [
  ...minOptionsOptions.filter((i) => i.value > 0),
  { value: 99, label: "Unlimited" },
]

const { handleSubmit, setValues, setErrors, resetForm } =
  useProductModifierGroupForm()

const onSubmit = handleSubmit(async (params) => {
  try {
    if (modifierGroupId.value) {
      const { id } = await updateProductModifier(
        modifierGroupId.value,
        params
      )
      confirm(id)
    } else {
      const { id } = await createProductModifier(params)
      confirm(id)
    }
  } catch (err: unknown) {
    /* surface server-side validation */
    if (err instanceof ApiError && err.status === 422) {
      setErrors(err.requestErrors?.body ?? {})
      return
    }
    throw err
  }
})

const {
  fields: optionFields,
  push: pushOption,
  remove: removeOption,
  replace,
} = useFieldArray("options")

const optionFieldsModel = computed({
  get: () => optionFields.value,
  set: (newFields) =>
    replace(newFields.map(({ value }) => value)),
})

const handleRemoveOption = (
  event: Event,
  index: number
) => {
  event.preventDefault()

  if (optionFields.value.length > 1) {
    removeOption(index)
  }
}

const addOption = () => {
  pushOption({
    label: "",
    priceOffset: 0,
    isSelected: false,
  })
}
const dragOptions = computed(() => ({
  animation: 200,
  ghostClass: $style.ghost,
  dragClass: $style.drag,
  direction: "vertical",
}))
</script>

<style lang="scss" module>
.ghost {
  opacity: 0;
}

.drag {
  opacity: 1;
}
</style>
